import { RotateCcw } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EYEBROW, FOCUS_RING } from '../constants';
import { CustomColorButton, Segmented, SliderRow } from '../controls';
import { pickerValue } from '../designUtils';

export const TINT_SWATCHES = [
  ['Navy', '#1F2A44'],
  ['Sage', '#4A6741'],
  ['Wine', '#8E4A3C'],
  ['Blush', '#F2C4CE'],
  ['Sky', '#9ED8F0'],
  ['Gold', '#B09536'],
  ['Grey', '#8C857A'],
];

const TINT_MODES = [
  ['multiply', 'Tint (multiply)'],
  ['color', 'Recolour'],
];

/**
 * "Change background colour": recolours the template artwork via
 * invitation.background = { tint_color, tint_mode, tint_strength } (null = original).
 * `compact` (Simple mode) shows only the swatches and "Original".
 */
export const BackgroundTintControls = ({ background, onChange, compact = false, title = 'Background colour' }) => {
  const tint = background?.tint_color ? background : null;
  const mode = tint?.tint_mode === 'color' ? 'color' : 'multiply';
  const strength = tint ? (Number.isFinite(Number(tint.tint_strength)) ? Number(tint.tint_strength) : 1) : 1;
  const current = (tint?.tint_color || '').toLowerCase();

  const set = (patch, key) =>
    onChange(
      {
        tint_color: tint?.tint_color || '#1F2A44',
        tint_mode: mode,
        tint_strength: strength,
        ...patch,
      },
      key
    );

  return (
    <div className="flex flex-col gap-2.5" data-testid="bg-tint">
      <p className={compact ? 'text-[13px] font-extrabold text-ink' : EYEBROW}>{title}</p>
      {!compact && (
        <Segmented label="Background colour mode" options={TINT_MODES} value={mode} onChange={(v) => set({ tint_mode: v })} />
      )}
      <div className="flex flex-wrap gap-2 items-center" role="group" aria-label="Background colours">
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-pressed={!tint}
          className={cn(
            'h-8 px-3 rounded-full text-xs font-extrabold flex items-center gap-1.5 border',
            !tint ? 'bg-ink text-paper border-ink' : 'bg-white text-ink border-[#DCD6C8] hover:bg-cream',
            FOCUS_RING
          )}
          data-testid="tint-original"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Original
        </button>
        {TINT_SWATCHES.map(([name, hex]) => {
          const on = current === hex.toLowerCase();
          return (
            <button
              key={hex}
              type="button"
              onClick={() => set({ tint_color: hex })}
              aria-label={`${name} background`}
              aria-pressed={on}
              title={name}
              className={cn(
                'h-8 w-8 rounded-full shrink-0',
                on ? 'ring-2 ring-ink ring-offset-2 ring-offset-white' : 'border border-[#DCD6C8]',
                FOCUS_RING
              )}
              style={{ backgroundColor: hex }}
              data-testid={`tint-${name.toLowerCase()}`}
            />
          );
        })}
        <CustomColorButton
          value={pickerValue(tint?.tint_color, '#1f2a44')}
          onChange={(e) => set({ tint_color: e.target.value }, 'tint-color')}
          label="Pick any background colour"
        />
      </div>
      {!compact && tint && (
        <SliderRow
          label="Strength"
          value={Math.round(strength * 100)}
          min={0}
          max={100}
          onChange={(v) => set({ tint_strength: v / 100 }, 'tint-strength')}
          format={(v) => `${v}%`}
        />
      )}
      {!compact && (
        <p className="text-xs leading-relaxed text-[#6B665C]">
          Tint colours the paper and keeps dark lines dark. Recolour changes the artwork&apos;s colours (gold ornaments to navy, for
          example) and keeps light and shade.
        </p>
      )}
    </div>
  );
};

export default BackgroundTintControls;
